import { useLocation } from 'react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePrevious from './usePrevious';
import { Namespace } from '../translation/namespace';
import { sendPageLockRelease } from './LeavingGuard/useLeavingGuard';
import { useAppContext } from '../contexts/AppContext/AppContext';

export default function useRedirect() {
  const location = useLocation();
  const { t } = useTranslation(Namespace.phrases);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();

  const previousLocationKey = usePrevious(location.key);
  const { editModeInfo, pageInfo } = useAppContext();

  const redirectCanceledMessage = t('redirectCanceled');

  // if user navigates to new page
  //  - by clicking on the linked redirect in forms
  useEffect(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    // only check changing location key as we want this effect to fire only if location changed by hand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // if component unmounts and location stays the same we assume user canceled redirect
  useEffect(() => {
    return () => {
      if (timeoutId && previousLocationKey === location.key) {
        // we used to send live region message here that forwarding was canceled but it was not the correct time to send
        clearTimeout(timeoutId);
      }
    };
  }, [location.key, previousLocationKey, redirectCanceledMessage, t, timeoutId]);

  const startRedirect = (redirectLocation: string | null) => {
    if (!timeoutId && redirectLocation) {
      const timeout: NodeJS.Timeout = setTimeout(() => {
        // need to release page lock when leaving edit mode via forwarding in modal
        sendPageLockRelease(editModeInfo.active, pageInfo?.pageLockReleaseUrl).then(() =>
          // using real reload here to better handle focusing elements with id -> #
          window.location.assign(redirectLocation)
        );
      }, 2000);
      setTimeoutId(timeout);
    }
  };

  return [startRedirect];
}
